<template>
  <div class="formation-page">
    <div class="container formation-page__container">
      <section class="formation-page__shell">
        <section class="formation-hero">
          <div class="formation-hero__media">
            <img :src="heroImage" :alt="heroAlt" loading="lazy" />
          </div>

          <div class="formation-hero__content">
            <OrderLesson :lesson="lesson" :inline-in-parent="true" />

            <p v-if="heroEyebrow" class="formation-hero__eyebrow">
              {{ heroEyebrow }}
            </p>
            <h1 v-html="heroTitle"></h1>

            <div v-if="heroStats.length" class="formation-hero__stats">
              <div
                v-for="stat in heroStats"
                :key="`${stat.label}-${stat.value}`"
                class="formation-hero__stat"
              >
                <span>{{ stat.label }}</span>
                <strong v-html="stat.value"></strong>
              </div>
            </div>
          </div>
        </section>

        <section class="formation-main">
          <div class="formation-page__back-row">
            <button
              name="button"
              type="button"
              class="formation-page__back"
              @click="goBackToFormations"
            >
              <span>&#171;</span> Retour formations
            </button>
          </div>

          <slot name="main"></slot>
        </section>
      </section>
    </div>

    <div class="formation-page__footer-line"></div>
  </div>
</template>

<script>
import OrderLesson from "@/components/plugins/OrderLesson.vue";

export default {
  name: "FormationDetailLayout",
  components: { OrderLesson },
  props: {
    lesson: {
      type: String,
      required: true,
    },
    backItem: {
      type: String,
      required: true,
    },
    heroImage: {
      type: String,
      required: true,
    },
    heroAlt: {
      type: String,
      default: "",
    },
    heroEyebrow: {
      type: String,
      default: "",
    },
    heroTitle: {
      type: String,
      required: true,
    },
    heroStats: {
      type: Array,
      default: () => [],
    },
  },
  methods: {
    goBackToFormations() {
      this.$router.push({
        path: "/formations-taxi-vtc",
        query: { itemActif: this.backItem },
      });
    },
  },
};
</script>

<style lang="scss">
.formation-page {
  padding-top: clamp(90px, 11vw, 132px);
}

.formation-page__container {
  padding-inline: clamp(14px, 3vw, 28px);
}

.formation-page__shell {
  width: min(100%, 1240px);
  margin: 0 auto;
}

.formation-page__back {
  width: fit-content;
  border: 0;
  padding: 0;
  background: transparent;
  color: rgba(0, 42, 84, 0.76);
  font-family: "adam";
  font-size: 0.68rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  transition: color 0.2s ease, transform 0.2s ease;

  span {
    margin-right: 8px;
    color: $color-secondary;
    font-size: 0.96rem;
  }

  &:hover {
    transform: translateX(-2px);
    color: $color-primary;

    span {
      color: $color-secondary;
    }
  }
}

.formation-page__back-row {
  display: flex;
  justify-content: flex-start;
  margin: -4px 0 2px;
}

.formation-hero {
  display: grid;
  gap: 0;
  overflow: hidden;
  border-radius: 34px;
  background: #ffffff;
  box-shadow: 0 24px 44px rgba(0, 42, 84, 0.12);

  @media screen and (min-width: 992px) {
    grid-template-columns: minmax(0, 1.08fr) minmax(360px, 0.92fr);
    min-height: 470px;
  }
}

.formation-hero__media {
  position: relative;
  min-height: 260px;
  background: $color-primary;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.formation-hero__content {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 20px;
  padding: clamp(16px, 2.6vw, 26px) clamp(24px, 4vw, 42px)
    clamp(24px, 4vw, 42px);
  background: $color-primary;
  color: $color-text-light;
}

.formation-hero__eyebrow {
  margin: 0;
  font-family: "adam";
  font-size: 0.72rem;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: $color-secondary;
}

.formation-hero__content h1 {
  margin: 0;
  font-family: "quantum";
  font-size: clamp(2rem, 3.8vw, 3rem);
  letter-spacing: 0.08em;
  line-height: 0.98;
  color: $color-text-light;
}

.formation-hero__stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.formation-hero__stat {
  min-height: 88px;
  padding: 14px 14px 16px;
  border-radius: 22px;
  border: 1px solid rgba(254, 185, 20, 0.16);
  background: rgba(0, 0, 0, 0.24);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 10px;

  span {
    display: block;
    font-family: "adam";
    font-size: 0.62rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: rgba(254, 185, 20, 0.86);
  }

  strong {
    display: block;
    font-family: "Spartan", "Arial Black", Arial, sans-serif;
    font-weight: 700;
    font-size: 0.94rem;
    line-height: 1.16;
    color: $color-text-light;
  }
}

.formation-main {
  display: flex;
  flex-direction: column;
  gap: 28px;
  margin-top: 30px;
}

.formation-section {
  border-radius: 30px;
  padding: clamp(22px, 4vw, 36px);
  background: #ffffff;
  box-shadow: 0 18px 36px rgba(0, 42, 84, 0.08);

  h2 {
    margin: 0 0 20px;
    color: $color-primary;
    font-size: clamp(1.45rem, 2.5vw, 2rem);
    text-align: left;
  }

  h3,
  h4,
  h5 {
    margin: 0;
    color: $color-primary;
    text-align: left;
  }

  p {
    margin: 0;
    color: $color-text;
    font-size: 1rem;
    line-height: 1.82;
  }
}

.formation-section--intro,
.formation-section--t3p {
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 8px;
    background: $color-secondary;
  }
}

.formation-section--theory {
  border-top: 8px solid $color-primary;
}

.formation-section--practice {
  border-top: 8px solid $color-secondary;
}

.formation-flow {
  display: grid;
  gap: 18px;
}

.formation-flow--tight {
  gap: 14px;
}

.formation-flow h3,
.formation-flow h4 {
  margin-bottom: 2px;
}

.formation-certification {
  margin-top: 24px;
  padding: 20px 22px;
  border-radius: 24px;
  background: $color-primary;
  box-shadow: 0 16px 28px rgba(0, 42, 84, 0.18);
}

.formation-certification__copy {
  color: $color-text-light !important;

  strong {
    color: $color-secondary;
    font-weight: 700;
  }
}

.formation-certification__link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
  padding: 10px 16px;
  border-radius: 999px;
  background: $color-secondary;
  color: $color-primary;
  font-family: "adam";
  font-size: 0.72rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  box-shadow: 0 8px 16px rgba(254, 185, 20, 0.18);
}

.formation-exams {
  display: grid;
  gap: 24px;

  @media screen and (min-width: 992px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
  }
}

.formation-exams__stack {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.formation-panel {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.formation-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;

  li {
    position: relative;
    padding: 16px 18px 16px 42px;
    border-radius: 22px;
    background: rgba(0, 42, 84, 0.04);
    color: $color-text;
    line-height: 1.62;
    box-shadow: inset 0 0 0 1px rgba(0, 42, 84, 0.08);

    &::before {
      content: "";
      position: absolute;
      top: 24px;
      left: 18px;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: $color-secondary;
      box-shadow: 0 0 0 6px rgba(254, 185, 20, 0.12);
    }
  }
}

.formation-list--compact {
  margin-top: 4px;
}

.formation-panel__footer {
  margin-top: 4px;
  padding: 18px 20px;
  border-radius: 22px;
  background: $color-primary;
  box-shadow: 0 14px 28px rgba(0, 42, 84, 0.16);

  p {
    color: $color-text-light;
    line-height: 1.7;
  }

  p + p {
    margin-top: 8px;
  }
}

.formation-page__footer-line {
  width: min(100%, 980px);
  height: 1px;
  background-color: rgba(0, 42, 84, 0.16);
  margin: 34px auto 100px;
}

@media screen and (max-width: 767px) {
  .formation-page {
    padding-top: 20px;
  }

  .formation-hero {
    border-radius: 26px;
  }

  .formation-hero__stats {
    grid-template-columns: 1fr;
  }

  .formation-hero__stat {
    min-height: auto;
  }

  .formation-section {
    border-radius: 24px;
  }

  .formation-list li {
    padding-left: 38px;
  }
}
</style>
